import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { InitialFallback, View } from '@khanacademy/wonder-blocks-core';
import { spacing } from '@khanacademy/wonder-blocks-tokens';
import { StyleSheet } from 'aphrodite';

const VALID_MEDIA_SIZES = ["small", "medium", "large"];
const mediaDefaultSpecLargeMarginWidth = spacing.large_24;
const MEDIA_DEFAULT_SPEC = {
  small: {
    query: "(max-width: 767px)",
    totalColumns: 4,
    gutterWidth: spacing.medium_16,
    marginWidth: spacing.medium_16
  },
  medium: {
    query: "(min-width: 768px) and (max-width: 1023px)",
    totalColumns: 8,
    gutterWidth: spacing.xLarge_32,
    marginWidth: spacing.large_24
  },
  large: {
    query: "(min-width: 1024px)",
    totalColumns: 12,
    gutterWidth: spacing.xLarge_32,
    marginWidth: mediaDefaultSpecLargeMarginWidth,
    maxWidth: 1120 + mediaDefaultSpecLargeMarginWidth * 2
  }
};
const MEDIA_INTERNAL_SPEC = {
  large: {
    query: "(min-width: 1px)",
    totalColumns: 12,
    gutterWidth: spacing.xLarge_32,
    marginWidth: spacing.medium_16
  }
};
const MEDIA_MODAL_SPEC = {
  small: {
    query: "(max-width: 767px)",
    totalColumns: 4,
    gutterWidth: spacing.medium_16,
    marginWidth: spacing.medium_16
  },
  large: {
    query: "(min-width: 768px)",
    totalColumns: 12,
    gutterWidth: spacing.xLarge_32,
    marginWidth: spacing.xxLarge_48
  }
};

const defaultContext = {
  ssrSize: "large",
  mediaSpec: MEDIA_DEFAULT_SPEC
};
const MediaLayoutContext = React.createContext(defaultContext);
MediaLayoutContext.displayName = "MediaLayoutContext";

const mediaQueryLists = {};
const DEFAULT_SIZE = "large";
class MediaLayoutInternal extends React.Component {
  constructor(props) {
    super(props);
    this.cleanupThunks = void 0;
    this.state = {
      size: undefined
    };
    this.cleanupThunks = [];
  }
  componentDidMount() {
    const entries = Object.entries(this.props.mediaSpec);
    for (const [size, spec] of entries) {
      const mql = mediaQueryLists[spec.query];
      if (!mql) {
        continue;
      }
      const listener = e => {
        if (e.matches) {
          this.setState({
            size
          });
        }
      };
      mql.addListener(listener);
      this.cleanupThunks.push(() => mql.removeListener(listener));
    }
  }
  componentWillUnmount() {
    this.cleanupThunks.forEach(cleaup => cleaup());
  }
  getCurrentSize(spec) {
    if (this.state.size) {
      return this.state.size;
    } else {
      const entries = Object.entries(this.props.mediaSpec);
      for (const [size, _spec] of entries) {
        const mql = mediaQueryLists[_spec.query];
        if (mql.matches) {
          return size;
        }
      }
    }
    return DEFAULT_SIZE;
  }
  isUnsupportedEnvironment() {
    return typeof window === "undefined" || !window.matchMedia;
  }
  getMockStyleSheet(mediaSize) {
    const {
      styleSheets
    } = this.props;
    const mockStyleSheet = {};
    if (!styleSheets) {
      return mockStyleSheet;
    }
    for (const styleSize of Object.keys(styleSheets)) {
      const styleSheet = styleSheets[styleSize];
      if (!styleSheet) {
        continue;
      }
      for (const name of Object.keys(styleSheet)) {
        if (Object.prototype.hasOwnProperty.call(mockStyleSheet, name)) {
          continue;
        }
        mockStyleSheet[name] = [styleSheets.all && styleSheets.all[name], mediaSize === "small" && [styleSheets.mdOrSmaller && styleSheets.mdOrSmaller[name], styleSheets.small && styleSheets.small[name]], mediaSize === "medium" && [styleSheets.mdOrSmaller && styleSheets.mdOrSmaller[name], styleSheets.mdOrLarger && styleSheets.mdOrLarger[name], styleSheets.medium && styleSheets.medium[name]], mediaSize === "large" && [styleSheets.mdOrLarger && styleSheets.mdOrLarger[name], styleSheets.large && styleSheets.large[name]]];
      }
    }
    return mockStyleSheet;
  }
  renderContent(initialRender) {
    const {
      children,
      mediaSpec,
      ssrSize,
      overrideSize
    } = this.props;
    const queries = [...Object.values(MEDIA_DEFAULT_SPEC).map(spec => spec.query), ...Object.values(MEDIA_INTERNAL_SPEC).map(spec => spec.query), ...Object.values(MEDIA_MODAL_SPEC).map(spec => spec.query), ...Object.values(mediaSpec).map(spec => spec.query)];
    if (!initialRender) {
      for (const query of queries.filter(query => !mediaQueryLists[query])) {
        mediaQueryLists[query] = window.matchMedia(query);
      }
    }
    const mediaSize = overrideSize || initialRender && ssrSize || this.getCurrentSize(mediaSpec);
    const styles = this.getMockStyleSheet(mediaSize);
    return children({
      mediaSize,
      mediaSpec,
      styles
    });
  }
  render() {
    return React.createElement(InitialFallback, {
      fallback: () => this.renderContent(true)
    }, () => this.renderContent(this.isUnsupportedEnvironment()));
  }
}
class MediaLayout extends React.Component {
  render() {
    return React.createElement(MediaLayoutContext.Consumer, null, ({
      overrideSize,
      ssrSize,
      mediaSpec
    }) => React.createElement(MediaLayoutInternal, _extends({}, this.props, {
      overrideSize: overrideSize,
      ssrSize: ssrSize,
      mediaSpec: mediaSpec
    })));
  }
}

class Spring extends React.Component {
  render() {
    const {
      style
    } = this.props;
    return React.createElement(View, {
      "aria-hidden": "true",
      style: [styles.grow, style]
    });
  }
}
const styles = StyleSheet.create({
  grow: {
    flexGrow: 1
  }
});

class Strut extends React.Component {
  render() {
    const {
      size,
      style
    } = this.props;
    return React.createElement(View, {
      "aria-hidden": "true",
      style: [strutStyle(size), style]
    });
  }
}
const strutStyle = size => {
  return {
    width: size,
    MsFlexBasis: size,
    MsFlexPreferredSize: size,
    WebkitFlexBasis: size,
    flexBasis: size,
    flexShrink: 0
  };
};

const queryMatchesSize = (mediaQuery, mediaSize) => {
  switch (mediaQuery) {
    case "all":
      return true;
    case "small":
      return mediaSize === "small";
    case "mdOrSmaller":
      return mediaSize === "medium" || mediaSize === "small";
    case "medium":
      return mediaSize === "medium";
    case "mdOrLarger":
      return mediaSize === "medium" || mediaSize === "large";
    case "large":
      return mediaSize === "large";
    default:
      throw new Error(`Unsupported mediaSize: ${mediaSize}`);
  }
};

export { MEDIA_DEFAULT_SPEC, MEDIA_INTERNAL_SPEC, MEDIA_MODAL_SPEC, MediaLayout, MediaLayoutContext, Spring, Strut, VALID_MEDIA_SIZES, queryMatchesSize };
